/**
 * Privacy Policy body rendered inside the Settings FullScreenSheet.
 * Mirrors Figma node 8315:71869 (Yaruta Design System v1). The standalone
 * marketing page at /privacy keeps its own richer layout; this component
 * is the condensed in-app version designed to be read on a phone without
 * leaving Settings. Keep this in sync with pages/PrivacyPolicy.tsx.
 */
export function PrivacyPolicyBody() {
  return (
    <div className="flex flex-col gap-3 text-base leading-6 text-foreground">
      {/* 16px medium #5e5e5e — Figma 10611:95789 (was 14px, YAR-180). */}
      <p className="text-base font-medium leading-6 text-muted-foreground">
        Effective Date: September 2026
      </p>

      <p>
        Yaruta (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) helps
        families organize school, sports, camp, and child-related logistics by
        transforming relevant email communications into clear summaries, tasks,
        and calendar-ready events.
      </p>
      <p>
        Yaruta services may be accessible through yaruta.app, getyaruta.com, and
        related product experiences.
      </p>

      <Section title="1. Information We Collect">
        <p>When you use Yaruta, we may collect:</p>
        <List>
          <li>
            Account information such as your name, email address, and login
            credentials
          </li>
          <li>Phone numbers you provide to receive text message reminders</li>
          <li>
            SMS/RCS consent and preference records, including when you opted in
            or out, the consent language presented to you, and your selected
            send time
          </li>
          <li>
            SMS/RCS delivery and interaction information, such as message status
            and STOP or HELP requests
          </li>
          <li>
            Connected email communications and metadata from Gmail or Outlook
            inboxes that you explicitly authorize Yaruta to access
          </li>
          <li>
            Child- and family-related email communications, including message
            content, sender information, timestamps, attachment metadata, and
            extracted event details from communications you authorize
          </li>
          <li>
            Images referenced by those communications. School announcements
            often place the date, time, and location only inside a flyer
            picture, so to read them Yaruta retrieves that image from whichever
            third-party service the sender used to host it (for example a
            newsletter provider&rsquo;s content network). Yaruta requests only
            images the message itself points to, sends no account information or
            cookies with the request, and processes the image solely to extract
            event details for your review
          </li>
          <li>
            AI-generated outputs, including summaries, extracted tasks,
            reminders, and calendar events
          </li>
          <li>
            Household information, such as collaborator profiles, parent roles,
            and child names, grades, and school information (optional)
          </li>
          <li>
            Usage data, including feature interactions, app diagnostics, and
            device/browser information
          </li>
        </List>
      </Section>

      <Section title="2. How We Use Your Information">
        <p>We use your information to:</p>
        <List>
          <li>
            Summarize school, sports, camp, and family-related email
            communications
          </li>
          <li>
            Extract tasks, deadlines, forms, reminders, and calendar events
          </li>
          <li>
            Help parents review and approve updates before adding them to their
            household schedule
          </li>
          <li>Sync approved items into the Yaruta household calendar</li>
          <li>
            Notify you about pending reviews and updates through the channels
            you enable
          </li>
          <li>
            Send recurring service-related SMS and/or RCS reminders and digests
            about your family&apos;s schedules, events, tasks, reminders, and
            items requiring review when you explicitly opt in
          </li>
          <li>
            Manage your SMS/RCS preferences, send time, delivery status, HELP
            requests, and opt-out requests
          </li>
          <li>
            Maintain records of SMS/RCS consent as required for compliance and
            service administration
          </li>
          <li>
            Improve extraction accuracy, product reliability, and user
            experience
          </li>
        </List>
      </Section>

      <Section title="3. Google API and Gmail Access">
        <p>
          If you connect Gmail, Yaruta accesses only the email communications
          and metadata necessary to provide the features you enable, such as
          summarizing school communications, extracting deadlines, generating
          reminders, and helping build your household schedule.
        </p>
        <p>
          Yaruta only processes communications relevant to the workflows you
          configure, such as:
        </p>
        <List>
          <li>Approved senders</li>
          <li>School domains</li>
          <li>Manually forwarded communications</li>
          <li>Other explicitly connected family logistics sources</li>
        </List>
        <p>
          Yaruta&apos;s use and transfer of information received from Google
          APIs will adhere to the{' '}
          <a
            className="underline font-medium"
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Google API Services User Data Policy
          </a>
          , including the Limited Use requirements.
        </p>
        <List>
          <li>We do not sell Google user data.</li>
          <li>We do not use Gmail data for advertising.</li>
          <li>
            We only use Google-connected email communications to provide the
            features clearly described in this policy.
          </li>
        </List>
        <p>
          <span className="font-semibold">
            Sender suggestions during onboarding.
          </span>{' '}
          When you connect Gmail, we perform a one-time scan of messages from
          the last 180 days (up to the 500 most recent), reading the{' '}
          <code>From</code> and <code>Subject</code> headers and the beginning
          of each message body (approximately the first 200 characters) to
          identify and rank likely senders for your allowlist. We do not store
          message bodies or attachments from this scan, and the scanned data is
          discarded once your suggestions are generated. Only the senders and
          domains you explicitly confirm are saved to your account.
        </p>
      </Section>

      {/* Twilio RCS sender review (YAR-462). Mirrored in pages/PrivacyPolicy.tsx. */}
      <Section title="4. SMS/RCS Communications">
        <p>
          If you explicitly opt in, Yaruta may send recurring service-related
          SMS and/or RCS messages about your family&apos;s schedules, events,
          tasks, reminders, daily digests, and items requiring review to the
          phone number you provide. SMS/RCS consent is optional, is collected
          separately from account creation, and is not required to use Yaruta.
        </p>
        <p>
          Message frequency varies. Message and data rates may apply. Reply STOP
          to cancel and opt out of future recurring messages. Reply HELP for
          help or contact{' '}
          <a
            className="underline font-medium"
            href="mailto:hello@yaruta.app"
          >
            hello@yaruta.app
          </a>
          . You can also turn off text message reminders in Yaruta&apos;s
          notification settings. Opting out of SMS/RCS does not prevent you from
          using other Yaruta features.
        </p>
        <p>
          You will not be held responsible for failing to opt out of Yaruta
          messages solely because your mobile phone number changes or is
          deactivated.
        </p>
        <p>
          We use service providers, including Twilio, to deliver and manage
          SMS/RCS messages. These providers receive information necessary to
          provide the messaging service, such as your phone number, message
          content, delivery information, and opt-out or help requests.
        </p>
        <p>
          We do not sell your phone number or SMS/RCS consent information. We do
          not share mobile information or SMS/RCS consent data with third
          parties or affiliates for their own marketing or promotional purposes.
          This does not include service providers acting on our behalf to
          deliver the messaging service.
        </p>
        <p>
          For the terms governing Yaruta&apos;s messaging program, please review
          our{' '}
          <a
            className="underline font-medium"
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
          >
            Terms &amp; Conditions
          </a>
          .
        </p>
      </Section>

      <Section title="5. Data Sharing">
        <p>We only share data in the following cases:</p>
        <List>
          <li>With members of your invited family household</li>
          <li>
            With service providers that host, secure, operate, or support
            Yaruta, including communications providers used to deliver messages
            you enable
          </li>
          <li>When required by law</li>
          <li>In the event of a merger, acquisition, or asset transfer</li>
        </List>
        <p>We never sell personal data.</p>
      </Section>

      <Section title="6. Data Retention">
        <p>
          We retain account data, extracted schedule information, and connected
          communication outputs while your account is active.
        </p>
        <p>
          We retain SMS/RCS consent and opt-out records for as long as
          reasonably necessary to document consent, honor messaging preferences,
          comply with legal and carrier requirements, and resolve disputes.
        </p>
        <p>
          You may request deletion of your data at any time by contacting us at{' '}
          <a
            className="underline font-medium"
            href="mailto:privacy@yaruta.app"
          >
            privacy@yaruta.app
          </a>
          . If you delete your account, your data is removed from active systems
          within approximately 30 days, except where we must retain limited
          information to comply with law, enforce opt-out requests, resolve
          disputes, or protect the service.
        </p>
      </Section>

      <Section title="7. Security">
        <p>
          We use commercially reasonable safeguards to protect your information,
          including encryption in transit and at rest, access controls, and
          least-privilege access for connected services.
        </p>
      </Section>

      <Section title="8. Children's Information">
        <p>
          Yaruta is designed for parents and caregivers, not for direct use by
          children.
        </p>
        <p>
          Any child-related information entered into Yaruta is provided by a
          parent, guardian, or authorized family household collaborator.
        </p>
      </Section>

      <Section title="9. Changes">
        <p>
          We may update this Privacy Policy as the product evolves. If we make
          material changes, we will update the effective date and provide notice
          as required. Your continued use of Yaruta after an updated policy
          takes effect is subject to the updated policy.
        </p>
      </Section>

      <Section title="10. Contact">
        <p>
          Questions can be sent to:{' '}
          <a
            className="underline font-medium"
            href="mailto:privacy@yaruta.app"
          >
            privacy@yaruta.app
          </a>
        </p>
      </Section>
    </div>
  );
}

// Small inline helpers — declared below to keep the body JSX short.
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2 mt-3">
      <h3 className="text-base font-semibold text-foreground">{title}</h3>
      {children}
    </section>
  );
}

function List({ children }: { children: React.ReactNode }) {
  return <ul className="list-disc pl-5 flex flex-col gap-1">{children}</ul>;
}
